/**
 * Access exploration component for admin panel.
 * Allows admins to explore access permissions for a device.
 */

import { useState } from 'react';
import { Button, Form, Card, Spinner, Alert, Badge, Accordion } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';

interface Device {
  id: number;
  device_id: string;
  description?: string;
  is_active: boolean;
}

interface DataType {
  id: number;
  name: string;
  description?: string;
}

interface Clinic {
  id: number;
  clinic_id: string;
  name: string;
  description?: string;
}

interface Study {
  id: number;
  study_id: string;
  name: string;
  description?: string;
}

interface Dataset {
  id: number;
  name?: string;
  data_type: DataType;
  clinic?: Clinic;
  study?: Study;
  start_time?: string;
  end_time?: string;
  created_at: string;
}

interface User {
  id: number;
  email: string;
  is_active: boolean;
  phone_verified: boolean;
}

interface UserGroup {
  id: number;
  name: string;
  description?: string;
  user_count: number;
  users: User[];
}

interface Role {
  id: number;
  name: string;
  description?: string;
  groups: UserGroup[];
}

interface AccessTree {
  device: Device;
  datasets: Dataset[];
  roles: Role[];
  total_users: number;
}

export default function AccessExploration() {
  const [deviceId, setDeviceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [accessTree, setAccessTree] = useState<AccessTree | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExplore = async () => {
    if (!deviceId.trim()) {
      toast.error('Please enter a device ID');
      return;
    }

    // Try to find device ID by device_id string first
    let actualDeviceId: number;

    try {
      setLoading(true);
      setError(null);
      setAccessTree(null);

      // First, try to get all devices to find the matching device_id
      const devicesResponse = await apiClient.getAdminDevices();
      if (devicesResponse.error) {
        throw new Error(devicesResponse.error);
      }

      const devices = devicesResponse.data as Array<{ id: number; device_id: string }>;
      const matchingDevice = devices.find(
        (d) => d.device_id.toLowerCase() === deviceId.trim().toLowerCase() || d.id.toString() === deviceId.trim()
      );

      if (!matchingDevice) {
        throw new Error(`Device with ID "${deviceId}" not found`);
      }

      actualDeviceId = matchingDevice.id;

      // Get access tree
      const response = await apiClient.exploreDeviceAccess(actualDeviceId);
      if (response.error) {
        throw new Error(response.error);
      }

      setAccessTree(response.data as AccessTree);
      toast.success('Access exploration completed');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to explore access';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="mb-1">Access Exploration</h2>
        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
          Explore access permissions for a device. View which datasets belong to the device, which roles have access,
          which groups have those roles, and which users are in those groups.
        </p>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleExplore();
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Device ID or Identifier</strong>
              </Form.Label>
              <Form.Control
                type="text"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="Enter device ID or device_id (e.g., 210A3*****061)"
                disabled={loading}
              />
              <Form.Text className="text-muted">
                You can enter either the device ID number or the device identifier string.
              </Form.Text>
            </Form.Group>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Exploring...
                </>
              ) : (
                'Explore Access'
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {accessTree && (
        <div>
          {/* Device Information */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">Device Information</h4>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-6">
                  <strong>Device ID:</strong> {accessTree.device.device_id}
                </div>
                <div className="col-md-6">
                  <strong>Status:</strong>{' '}
                  <Badge bg={accessTree.device.is_active ? 'success' : 'danger'}>
                    {accessTree.device.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                {accessTree.device.description && (
                  <div className="col-12 mt-2">
                    <strong>Description:</strong> {accessTree.device.description}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Datasets */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">
                Datasets ({accessTree.datasets.length}){' '}
                <Badge bg="info">{accessTree.datasets.length} dataset(s) found</Badge>
              </h4>
            </Card.Header>
            <Card.Body>
              {accessTree.datasets.length === 0 ? (
                <Alert variant="info">No datasets found for this device.</Alert>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Data Type</th>
                        <th>Clinic</th>
                        <th>Study</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessTree.datasets.map((dataset) => (
                        <tr key={dataset.id}>
                          <td>{dataset.id}</td>
                          <td>{dataset.name || '-'}</td>
                          <td>
                            <Badge bg="primary">{dataset.data_type.name}</Badge>
                          </td>
                          <td>{dataset.clinic ? `${dataset.clinic.name} (${dataset.clinic.clinic_id})` : '-'}</td>
                          <td>{dataset.study ? `${dataset.study.name} (${dataset.study.study_id})` : '-'}</td>
                          <td>{formatDateTime(dataset.start_time)}</td>
                          <td>{formatDateTime(dataset.end_time)}</td>
                          <td>{formatDateTime(dataset.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Roles and Access Chain */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">
                Access Chain ({accessTree.roles.length} role(s), {accessTree.total_users} total user(s))
              </h4>
            </Card.Header>
            <Card.Body>
              {accessTree.roles.length === 0 ? (
                <Alert variant="warning">
                  No roles have access to this device. Users will not be able to access datasets from this device.
                </Alert>
              ) : (
                <Accordion defaultActiveKey="0">
                  {accessTree.roles.map((role, roleIdx) => (
                    <Accordion.Item eventKey={roleIdx.toString()} key={role.id}>
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100 me-3">
                          <Badge bg="secondary" className="me-2">
                            Role
                          </Badge>
                          <strong>{role.name}</strong>
                          {role.description && (
                            <span className="text-muted ms-2">- {role.description}</span>
                          )}
                          <Badge bg="info" className="ms-auto me-2">
                            {role.groups.length} group(s)
                          </Badge>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        {role.groups.length === 0 ? (
                          <Alert variant="info">This role is not assigned to any user groups.</Alert>
                        ) : (
                          <Accordion>
                            {role.groups.map((group, groupIdx) => (
                              <Accordion.Item eventKey={`${roleIdx}-${groupIdx}`} key={group.id}>
                                <Accordion.Header>
                                  <div className="d-flex align-items-center w-100 me-3">
                                    <Badge bg="success" className="me-2">
                                      Group
                                    </Badge>
                                    <strong>{group.name}</strong>
                                    {group.description && (
                                      <span className="text-muted ms-2">- {group.description}</span>
                                    )}
                                    <Badge bg="warning" className="ms-auto me-2" text="dark">
                                      {group.user_count} user(s)
                                    </Badge>
                                  </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                  {group.users.length === 0 ? (
                                    <Alert variant="info">No users in this group.</Alert>
                                  ) : (
                                    <div className="table-responsive">
                                      <table className="table table-sm table-striped">
                                        <thead>
                                          <tr>
                                            <th>ID</th>
                                            <th>Email</th>
                                            <th>Status</th>
                                            <th>Phone Verified</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {group.users.map((user) => (
                                            <tr key={user.id}>
                                              <td>{user.id}</td>
                                              <td>{user.email}</td>
                                              <td>
                                                <Badge bg={user.is_active ? 'success' : 'danger'}>
                                                  {user.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                              </td>
                                              <td>
                                                <Badge bg={user.phone_verified ? 'success' : 'warning'} text="dark">
                                                  {user.phone_verified ? 'Verified' : 'Not Verified'}
                                                </Badge>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            ))}
                          </Accordion>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Card.Body>
          </Card>

          {/* Summary */}
          <Card>
            <Card.Header>
              <h4 className="mb-0">Summary</h4>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="text-primary mb-1">{accessTree.datasets.length}</h3>
                    <small className="text-muted">Datasets</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="text-info mb-1">{accessTree.roles.length}</h3>
                    <small className="text-muted">Roles</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="text-success mb-1">
                      {accessTree.roles.reduce((sum, role) => sum + role.groups.length, 0)}
                    </h3>
                    <small className="text-muted">User Groups</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="text-warning mb-1">{accessTree.total_users}</h3>
                    <small className="text-muted">Total Users</small>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
}
